/**
 * @fileoverview Builds the right-panel body for the inline card help affordance
 * ("What are experiments?" / "What are insights?"). Consumes the structured
 * content in `js/help-content/index.js` and renders: lede → annotated specimen
 * → steps/sources.
 *
 * The specimen is a hand-built figure rather than a call into the live card
 * renderers, deliberately:
 *   - `renderPracticeProposal` clears live Advice terminus pills unconditionally
 *     (NOT gated on `interactive`, see adviceTerminusPills.js), so invoking it to
 *     draw a help illustration would silently remove a standing pill row.
 *   - The figure needs per-part annotation anchors the real renderers do not
 *     expose, and wants fixed minimal content rather than whatever the model
 *     emitted.
 * It reuses the real card's CSS classes where the visual contract is shared
 * (`sd-takeaway`, `sd-section-header`), so those stay in step automatically.
 *
 * @module chat/helpHintPanel
 */

import { getActionHelp, getHelpContent } from '../help-content/index.js';

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text] - Assigned via textContent; never innerHTML
 * @returns {HTMLElement}
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null && text !== '') node.textContent = text;
  return node;
}

/**
 * Render one specimen part into the figure's right-hand column.
 * @param {object} part
 * @returns {HTMLElement|null}
 */
function buildSpecimenPart(part) {
  switch (part.type) {
    case 'label':
      return el('div', 'help-hint-panel__specimen-label', part.text);

    case 'title':
      return el('div', 'help-hint-panel__specimen-title', part.text);

    case 'body':
      return el('div', 'help-hint-panel__specimen-body', part.text);

    case 'pills': {
      const row = el('div', 'help-hint-panel__specimen-pills');
      if (part.primary) {
        row.appendChild(el('span', 'help-hint-panel__specimen-pill help-hint-panel__specimen-pill--primary', part.primary));
      }
      if (part.secondary) {
        row.appendChild(el('span', 'help-hint-panel__specimen-pill help-hint-panel__specimen-pill--secondary', part.secondary));
      }
      return row;
    }

    case 'callout': {
      // Shared treatment with the real card's "Why This Matters" box.
      const box = el('div', 'sd-takeaway help-hint-panel__specimen-callout');
      box.appendChild(el('div', 'sd-takeaway__label', part.label));
      box.appendChild(el('p', 'sd-takeaway__text', part.text));
      return box;
    }

    default:
      return null;
  }
}

/**
 * Build the annotated specimen figure: a two-column grid whose left gutter
 * carries the annotation and whose right column carries the card parts.
 * @param {{rows: Array}} specimen
 * @returns {HTMLElement|null}
 */
function buildSpecimen(specimen) {
  const rows = Array.isArray(specimen?.rows) ? specimen.rows : [];
  if (rows.length === 0) return null;

  const figure = el('div', 'help-hint-panel__specimen');
  figure.setAttribute('aria-hidden', 'true'); // Decorative: the prose carries the meaning

  for (const row of rows) {
    figure.appendChild(el('div', 'help-hint-panel__annotation', row.annotation));

    const parts = el('div', 'help-hint-panel__specimen-parts');
    for (const part of row.parts || []) {
      const node = buildSpecimenPart(part);
      if (node) parts.appendChild(node);
    }
    figure.appendChild(parts);
  }

  return figure;
}

/**
 * Build the closing beat — its own H2 over a boxed list: numbered steps
 * (practices) or an unnumbered source list (insights).
 * @param {{header: string, numbered: boolean, items: string[]}} section
 * @returns {DocumentFragment|null}
 */
function buildSection(section) {
  const items = Array.isArray(section?.items) ? section.items : [];
  if (items.length === 0) return null;

  const frag = document.createDocumentFragment();
  frag.appendChild(el('h2', 'help-hint-panel__heading help-hint-panel__heading--section', section.header));

  const box = el('div', 'help-hint-panel__list-box');
  const list = el(section.numbered ? 'ol' : 'ul', 'help-hint-panel__steps');
  if (!section.numbered) list.classList.add('help-hint-panel__steps--plain');

  for (const item of items) {
    list.appendChild(el('li', 'help-hint-panel__step', item));
  }
  box.appendChild(list);
  frag.appendChild(box);
  return frag;
}

/**
 * Build the full right-panel payload for a card help type.
 *
 * The panel opens with NO chrome title — the in-body `heading` carries it, the
 * same arrangement the insight detail panel uses.
 *
 * @param {'practices'|'insights'} type
 * @returns {{title: string, content: HTMLElement}|null} Null for unknown types
 */
export function buildHelpHintPanel(type) {
  const content = getHelpContent(type);
  if (!content) return null;

  const body = el('div', 'help-hint-panel');

  if (content.heading) body.appendChild(el('h2', 'help-hint-panel__heading', content.heading));
  if (content.lede) body.appendChild(el('p', 'help-hint-panel__lede', content.lede));

  const specimen = buildSpecimen(content.specimen);
  if (specimen) body.appendChild(specimen);

  const section = buildSection(content.section);
  if (section) body.appendChild(section);

  return { title: '', content: body };
}

/**
 * Build the right-panel payload for the chat-input Actions menu's
 * "Learn about actions" footer.
 *
 * Stacked short sections rather than an annotated specimen: the menu offers up
 * to three heterogeneous actions, and the reader's question is "what does each
 * row do", not "what is this card". One block per entry, in menu order, built
 * from `help-content`'s `getActionHelp` — entries are the resolved menu items
 * passed at click time, so the panel describes exactly what is on offer.
 *
 * Same no-chrome-title contract as {@link buildHelpHintPanel}.
 *
 * @param {Array<{key: string}>} entries - Resolved Actions-menu items
 * @returns {{title: string, content: HTMLElement}|null} Null when nothing resolves
 */
export function buildActionsHelpPanel(entries) {
  const blocks = (entries || [])
    .map((entry) => ({ key: entry.key, help: getActionHelp(entry.key) }))
    .filter((block) => block.help);
  if (blocks.length === 0) return null;

  const body = el('div', 'help-hint-panel');
  body.appendChild(el('h2', 'help-hint-panel__heading', 'Actions'));
  body.appendChild(el('p', 'help-hint-panel__lede',
    'Shortcuts for where this conversation can go. Each one is also available just by asking.'));

  for (const { help } of blocks) {
    const block = el('div', 'help-hint-panel__action');
    const titleRow = el('div', 'help-hint-panel__action-title');
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'help-hint-panel__action-icon');
    icon.setAttribute('width', '22');
    icon.setAttribute('height', '22');
    icon.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', `#icon-${help.icon}`);
    icon.appendChild(use);
    titleRow.appendChild(icon);
    titleRow.appendChild(el('span', '', help.title));
    block.appendChild(titleRow);
    block.appendChild(el('p', 'help-hint-panel__action-body', help.body));
    body.appendChild(block);
  }

  return { title: '', content: body };
}
